export const FEEDBACK_LIMITS = {
  comment: 5000,
  expected: 5000,
  path: 2048,
  feedbackId: 500,
  userAgent: 1024,
  appVersion: 200,
  viewportMax: 100000,
  screenshotChars: 1500000,
  thumbnailChars: 150000,
} as const;

export const FEEDBACK_ID_SOURCES = ['attribute', 'css-path'] as const;
export type FeedbackIdSource = (typeof FEEDBACK_ID_SOURCES)[number];

export interface FeedbackContextValue {
  path: string;
  feedbackId: string;
  feedbackIdSource: FeedbackIdSource;
  viewport: { width: number; height: number };
  userAgent: string;
  appVersion: string | null;
}

export interface FeedbackInputValue {
  comment: string;
  expected: string | null;
  screenshot: string | null;
  thumbnail: string | null;
  context: FeedbackContextValue;
}

export type FeedbackValidation =
  | { ok: true; value: FeedbackInputValue }
  | { ok: false; error: string };

const JPEG_DATA_URL = /^data:image\/jpeg;base64,[A-Za-z0-9+/]+={0,2}$/;

function optionalText(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function checkImage(
  value: unknown,
  label: string,
  maxChars: number
): { ok: true; value: string | null } | { ok: false; error: string } {
  if (value == null || value === '') {
    return { ok: true, value: null };
  }
  if (typeof value !== 'string') {
    return { ok: false, error: `${label} must be a JPEG data URL` };
  }
  if (value.length > maxChars) {
    return { ok: false, error: `${label} is too large` };
  }
  if (!JPEG_DATA_URL.test(value)) {
    return { ok: false, error: `${label} must be a JPEG data URL` };
  }
  return { ok: true, value };
}

function isViewportSide(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value > 0 &&
    value <= FEEDBACK_LIMITS.viewportMax
  );
}

export function validateFeedbackInput(raw: unknown): FeedbackValidation {
  const input = (raw ?? {}) as Record<string, any>;

  const comment = optionalText(input.comment);
  if (!comment) {
    return { ok: false, error: 'A comment is required' };
  }
  if (comment.length > FEEDBACK_LIMITS.comment) {
    return { ok: false, error: 'The comment is too long' };
  }

  const expected = optionalText(input.expected);
  if (expected && expected.length > FEEDBACK_LIMITS.expected) {
    return { ok: false, error: 'The expected-behaviour note is too long' };
  }

  const screenshot = checkImage(
    input.screenshot,
    'The screenshot',
    FEEDBACK_LIMITS.screenshotChars
  );
  if (!screenshot.ok) {
    return screenshot;
  }
  const thumbnail = checkImage(
    input.thumbnail,
    'The thumbnail',
    FEEDBACK_LIMITS.thumbnailChars
  );
  if (!thumbnail.ok) {
    return thumbnail;
  }

  const context = (input.context ?? {}) as Record<string, any>;

  const path = optionalText(context.path);
  if (!path?.startsWith('/') || path.length > FEEDBACK_LIMITS.path) {
    return { ok: false, error: 'The page path is missing or invalid' };
  }

  const feedbackId = optionalText(context.feedbackId);
  if (!feedbackId || feedbackId.length > FEEDBACK_LIMITS.feedbackId) {
    return { ok: false, error: 'The page region is missing or invalid' };
  }

  const feedbackIdSource = context.feedbackIdSource;
  if (!FEEDBACK_ID_SOURCES.includes(feedbackIdSource)) {
    return { ok: false, error: 'The page region source is invalid' };
  }

  const viewport = (context.viewport ?? {}) as Record<string, unknown>;
  if (!isViewportSide(viewport.width) || !isViewportSide(viewport.height)) {
    return { ok: false, error: 'The viewport size is invalid' };
  }

  const userAgent = optionalText(context.userAgent) ?? '';
  if (userAgent.length > FEEDBACK_LIMITS.userAgent) {
    return { ok: false, error: 'The user agent is too long' };
  }

  const appVersion = optionalText(context.appVersion);
  if (appVersion && appVersion.length > FEEDBACK_LIMITS.appVersion) {
    return { ok: false, error: 'The app version is too long' };
  }

  return {
    ok: true,
    value: {
      comment,
      expected,
      screenshot: screenshot.value,
      thumbnail: thumbnail.value,
      context: {
        path,
        feedbackId,
        feedbackIdSource,
        viewport: { width: viewport.width, height: viewport.height },
        userAgent,
        appVersion,
      },
    },
  };
}
